import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppointmentRequest } from "api/types";
import { setCurrentPatient } from "../../redux/users/actions";
import { setStoragePatient } from "../../utils/storageUtils";

interface CalendarViewProps {
  appointments: AppointmentRequest[];
  onAppointmentClick?: (appointment: AppointmentRequest) => void;
}

const CalendarView: React.FC<CalendarViewProps> = ({
  appointments,
  onAppointmentClick,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentDate, setCurrentDate] = React.useState(new Date());
  const [expandedDays, setExpandedDays] = React.useState<Set<string>>(new Set());

  const handleAppointmentClick = (appointment: AppointmentRequest) => {
    if (appointment.patient) {
      // Set patient in Redux store and localStorage
      dispatch(setCurrentPatient(appointment.patient));
      setStoragePatient(appointment.patient);
      // Navigate to consultation page
      navigate("/consultation");
    }
    // Call custom handler if provided
    if (onAppointmentClick) {
      onAppointmentClick(appointment);
    }
  };

  // Check if appointment was created by AI
  const isAICreated = (appointment: AppointmentRequest) => {
    const description = appointment?.description || "";
    return (
      description?.includes("RDV réservé via l'IA") ||
      description?.includes("RDV synchronisé depuis Google Calendar") ||
      description?.includes("via l'IA")
    );
  };

  // Get first day of month
  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  // Get last day of month
  const getLastDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  };

  // Get days in month
  const getDaysInMonth = (date: Date) => {
    return getLastDayOfMonth(date).getDate();
  };

  // Get day of week for first day of month (0 = Sunday, 6 = Saturday)
  const getFirstDayOfWeek = (date: Date) => {
    return getFirstDayOfMonth(date).getDay();
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    );
  };

  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );
  };

  // Navigate to previous year
  const goToPreviousYear = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear() - 1, currentDate.getMonth(), 1)
    );
  };

  // Navigate to next year
  const goToNextYear = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), 1)
    );
  };

  // Navigate to today
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get appointments for a specific date
  const getAppointmentsForDate = (date: Date) => {
    // Use local date format to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    return appointments.filter((apt) => {
      if (!apt.appointmentDate) return false;
      const aptDate = new Date(apt.appointmentDate);
      // Use local date format to avoid timezone issues
      const aptYear = aptDate.getFullYear();
      const aptMonth = String(aptDate.getMonth() + 1).padStart(2, '0');
      const aptDay = String(aptDate.getDate()).padStart(2, '0');
      const aptDateStr = `${aptYear}-${aptMonth}-${aptDay}`;
      
      return aptDateStr === dateStr;
    });
  };

  // Format time to HH:mm (without seconds)
  const formatTime = (timeString: string | undefined): string => {
    if (!timeString) return "";
    // Handle different time formats (HH:mm:ss or HH:mm)
    const parts = timeString.split(":");
    if (parts.length >= 2) {
      return `${parts[0]}:${parts[1]}`;
    }
    return timeString;
  };

  // Toggle expanded state for a day
  const toggleDayExpansion = (dateStr: string) => {
    setExpandedDays((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(dateStr)) {
        newSet.delete(dateStr);
      } else {
        newSet.add(dateStr);
      }
      return newSet;
    });
  };

  // Format date for display
  const formatMonthYear = (date: Date) => {
    const months = [
      "janvier",
      "février",
      "mars",
      "avril",
      "mai",
      "juin",
      "juillet",
      "août",
      "septembre",
      "octobre",
      "novembre",
      "décembre",
    ];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Check if date is today
  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Render calendar grid
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDayOfWeek = getFirstDayOfWeek(currentDate);
    const days: (Date | null)[] = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(
        new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
      );
    }

    // Group days into weeks (7 days per week)
    const weeks: (Date | null)[][] = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    // Ensure last week has 7 days
    if (weeks.length > 0 && weeks[weeks.length - 1].length < 7) {
      const lastWeek = weeks[weeks.length - 1];
      while (lastWeek.length < 7) {
        lastWeek.push(null);
      }
    }

    const weekDays = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

    return (
      <div className="w-full">
        {/* Calendar Header */}
        <div className="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={goToPreviousYear}
              className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
              title="Année précédente"
            >
              <i className="bx bx-chevrons-left text-lg"></i>
            </button>
            <button
              onClick={goToPreviousMonth}
              className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
              title="Mois précédent"
            >
              <i className="bx bx-chevron-left text-xl"></i>
            </button>
          </div>
          <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4">
            <div className="flex items-center space-x-2">
              <h2 className="text-lg sm:text-xl font-semibold text-slate-700">
                {formatMonthYear(currentDate)}
              </h2>
              <div className="flex items-center space-x-1">
                <button
                  onClick={goToPreviousYear}
                  className="px-2 py-1 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded hover:bg-slate-50 transition-colors"
                  title="Année précédente"
                >
                  <i className="bx bx-chevron-up text-sm"></i>
                </button>
                <button
                  onClick={goToNextYear}
                  className="px-2 py-1 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded hover:bg-slate-50 transition-colors"
                  title="Année suivante"
                >
                  <i className="bx bx-chevron-down text-sm"></i>
                </button>
              </div>
            </div>
            <button
              onClick={goToToday}
              className="px-4 py-2 text-sm font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-colors"
            >
              Aujourd'hui
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={goToNextMonth}
              className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
              title="Mois suivant"
            >
              <i className="bx bx-chevron-right text-xl"></i>
            </button>
            <button
              onClick={goToNextYear}
              className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
              title="Année suivante"
            >
              <i className="bx bx-chevrons-right text-lg"></i>
            </button>
          </div>
        </div>

        {/* Calendar Table - Google Calendar style */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm overflow-x-auto">
          <table className="w-full border-collapse" style={{ minWidth: '600px' }}>
            <thead>
              <tr className="bg-slate-50/70 border-b border-slate-200">
                {weekDays.map((day, idx) => (
                  <th
                    key={idx}
                    className="px-2 py-3 text-xs sm:text-sm font-semibold text-slate-500 text-center border-r border-slate-200 last:border-r-0"
                  >
                    {day}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, weekIdx) => (
                <tr key={weekIdx} className="border-b border-slate-200 last:border-b-0">
                  {week.map((date, dayIdx) => {
                    if (!date) {
                      return (
                        <td
                          key={`empty-${weekIdx}-${dayIdx}`}
                          className="h-24 sm:h-32 border-r border-slate-200 last:border-r-0 bg-slate-50 align-top"
                        />
                      );
                    }

                    const dayAppointments = getAppointmentsForDate(date);
                    const today = isToday(date);
                    const isOtherMonth = date.getMonth() !== currentDate.getMonth();
                    const dateStr = date.toISOString().split("T")[0];
                    const isExpanded = expandedDays.has(dateStr);
                    const maxVisible = 3;
                    const visibleAppointments = isExpanded 
                      ? dayAppointments 
                      : dayAppointments.slice(0, maxVisible);
                    const hasMore = dayAppointments.length > maxVisible;

                    return (
                      <td
                        key={date.toISOString()}
                        className={`h-24 sm:h-32 border-r border-slate-200 last:border-r-0 p-1 align-top ${
                          today
                            ? "bg-green-50 hover:bg-green-100/60"
                            : isOtherMonth
                            ? "bg-slate-50 hover:bg-emerald-50/40"
                            : "bg-white hover:bg-emerald-50/40"
                        } transition-colors`}
                      >
                        <div className="flex flex-col h-full">
                          <div
                            className={`text-xs font-medium mb-1 ${
                              today
                                ? "text-green-600 font-bold"
                                : isOtherMonth
                                ? "text-slate-400"
                                : "text-slate-700"
                            }`}
                          >
                            {date.getDate()}
                          </div>
                          <div className="flex-1 overflow-y-auto space-y-1 min-h-0">
                            {dayAppointments.length === 0 ? (
                              <div className="text-xs text-slate-400 text-center py-2">
                                Aucun rendez-vous
                              </div>
                            ) : (
                              <>
                                {visibleAppointments.map((apt, aptIdx) => {
                                  const aiCreated = isAICreated(apt);
                                  return (
                                    <div
                                      key={aptIdx}
                                      onClick={() => handleAppointmentClick(apt)}
                                      className={`text-xs px-2 py-1 rounded cursor-pointer ${
                                        aiCreated
                                          ? "bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-emerald-200"
                                          : "bg-green-100 text-green-700 border border-green-300 hover:bg-green-200"
                                      } transition-colors mb-1`}
                                      title={`${formatTime(apt.appointmentTime)} - ${
                                        apt.patient?.firstName || ""
                                      } ${apt.patient?.lastName || ""} - ${
                                        apt.appointmentObject || ""
                                      }`}
                                    >
                                      <div className="flex items-center space-x-1 mb-0.5">
                                        {aiCreated && (
                                          <i className="bx bx-bot text-xs flex-shrink-0"></i>
                                        )}
                                        <span className="font-semibold text-xs">
                                          {formatTime(apt.appointmentTime)}
                                        </span>
                                      </div>
                                      <div className="font-medium truncate">
                                        {apt.patient?.firstName || ""}{" "}
                                        {apt.patient?.lastName || ""}
                                      </div>
                                      {apt.appointmentObject && (
                                        <div className="text-xs opacity-75 truncate mt-0.5">
                                          {apt.appointmentObject}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                                {hasMore && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleDayExpansion(dateStr);
                                    }}
                                    className="text-xs text-green-600 hover:text-green-700 hover:underline font-medium w-full text-left px-1 py-0.5 mt-1"
                                  >
                                    {isExpanded 
                                      ? `Voir moins (-${dayAppointments.length - maxVisible})` 
                                      : `Voir plus (+${dayAppointments.length - maxVisible})`}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="mt-6 p-4 bg-slate-50/70 border border-slate-200 rounded-lg">
          <div className="flex flex-wrap items-center justify-center text-sm">
            <div className="flex items-center space-x-3 pr-8 sm:pr-12">
              <div className="w-5 h-5 bg-green-100 border-2 border-green-400 rounded shadow-sm flex-shrink-0"></div>
              <span className="font-medium text-slate-700">Créé manuellement</span>
            </div>
            <div className="flex items-center space-x-3 pl-8 sm:pl-12">
              <div className="w-5 h-5 bg-emerald-100 border-2 border-emerald-400 rounded shadow-sm flex-shrink-0 relative">
                <i className="bx bx-bot text-emerald-600 text-xs absolute inset-0 flex items-center justify-center"></i>
              </div>
              <span className="font-medium text-slate-700">Réservé automatiquement</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-full">{renderCalendar()}</div>
    </div>
  );
};

export default CalendarView;

